// Which boards can hold a given number of canvases, and what they cost.
//
// A board is cut to size - its grid of canvases times the canvas size - from
// a stock sheet. The sheets (and what a board cut from each costs) are set on
// the admin panel's Website page and come with the catalog: a small, cheap
// one most boards fit on, and a bigger, dearer one for those that don't.
// The price is the cheapest sheet the board fits on - the same rule the
// server applies when the order is placed (backend/shop.py) - so a board that
// outgrows the small sheet moves up to the bigger one, and its price, by
// itself.
//
// A number of painters rarely makes a perfect rectangle, so instead of
// forcing one near-square grid (and whatever gaps that leaves), the order
// page offers the few shapes that fit best and says how many spots each
// leaves over. Every spot on a finished board is filled, so an empty spot
// means another canvas - fewer is cheaper.

// Wider than 3:1 stops looking like a board and starts looking like a shelf.
const MAX_ASPECT = 3;
const MAX_OPTIONS = 3;
// Ties on empty spots and price go to whichever is closest to a 4:3 picture frame.
const IDEAL_ASPECT = 4 / 3;
// The most spots a board has - the server refuses more (backend/schemas.py).
export const MAX_SPOTS = 200;

const sideOf = (sheet) => ({
  long: Math.max(sheet.width_cm, sheet.height_cm),
  short: Math.min(sheet.width_cm, sheet.height_cm),
});

/** The cheapest sheet a width x height board can be cut from (sheets can be
 *  turned), or null when even the biggest is too small. Same tie-break as
 *  the server: the smaller sheet, then the lower id. */
export function sheetFor(sheets, widthCm, heightCm) {
  const long = Math.max(widthCm, heightCm);
  const short = Math.min(widthCm, heightCm);
  return (
    sheets
      .filter((sheet) => long <= sideOf(sheet).long && short <= sideOf(sheet).short)
      .sort(
        (a, b) =>
          a.price_cents - b.price_cents ||
          a.width_cm * a.height_cm - b.width_cm * b.height_cm ||
          a.id - b.id
      )[0] ?? null
  );
}

/** Shapes for `count` canvases of `sizeCm` that can be cut from some sheet,
 *  best first, as landscape {cols, rows, spare, sheet}. Fewest empty spots
 *  first, then the cheapest sheet, then the most picture-frame-like. Portrait
 *  is the same shape turned on its side - see orient() below. */
export function layoutOptions(count, sizeCm, sheets) {
  const n = Math.max(1, Math.floor(count));
  const offIdeal = (o) => Math.abs(Math.log(o.cols / o.rows / IDEAL_ASPECT));

  function shapes(maxAspect) {
    const options = [];
    // Every row count up to the square: the columns needed to fit everyone,
    // which also keeps the empty spots to less than one row's worth.
    for (let rows = 1; ; rows += 1) {
      const cols = Math.ceil(n / rows);
      if (cols < rows) break;
      if (cols / rows > maxAspect || cols * rows > MAX_SPOTS) continue;
      const sheet = sheetFor(sheets, cols * sizeCm, rows * sizeCm);
      if (sheet) options.push({ cols, rows, spare: cols * rows - n, sheet });
    }
    return options;
  }

  // Near the top of what a sheet carries, only a long thin shape may still
  // fit: better that than offering nothing.
  let options = shapes(MAX_ASPECT);
  if (options.length === 0) options = shapes(Infinity);

  options.sort(
    (a, b) =>
      a.spare - b.spare ||
      a.sheet.price_cents - b.sheet.price_cents ||
      offIdeal(a) - offIdeal(b)
  );
  return options.slice(0, MAX_OPTIONS);
}

/** Whether a width x height board, packed, fits the biggest parcel the
 *  carrier takes - the same rule as shop.board_fits_parcel() on the server.
 *  Boards that don't can only be picked up. No limit known: assume it fits
 *  and let the server decide. */
export function fitsParcel(parcel, widthCm, heightCm) {
  if (!parcel) return true;
  const margin = parcel.packing_margin_cm;
  const parcelLong = Math.max(parcel.max_length_cm, parcel.max_width_cm);
  const parcelShort = Math.min(parcel.max_length_cm, parcel.max_width_cm);
  return (
    Math.max(widthCm, heightCm) + margin <= parcelLong && Math.min(widthCm, heightCm) + margin <= parcelShort
  );
}

/** The shape as it will actually hang: turned upright for "portrait". */
export function orient(shape, orientation) {
  return orientation === 'portrait' ? { ...shape, cols: shape.rows, rows: shape.cols } : shape;
}

/** The most canvases of this size that still make a board: `count`, or the
 *  nearest number below it that does. Near the top of what the biggest sheet
 *  carries (or of MAX_SPOTS) a count can fall between two shapes that fit. */
export function fitCount(count, sizeCm, sheets) {
  for (let n = Math.min(Math.floor(count), MAX_SPOTS); n > 1; n -= 1) {
    if (layoutOptions(n, sizeCm, sheets).length > 0) return n;
  }
  return 1;
}
